import React, { useContext, useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import moment from "moment"
import Select from "react-select"
import "react-datepicker/dist/react-datepicker.css"
import { daysInWeek, hours } from "../../../objects/timeObjects"
import { FilterType } from "../../../types/schedulingTypes"
import { Meeting } from "../../../types/meetingTypes"
import { MeetingRequests } from "../../../api/app.service"
import {
  SvgFilledInformationCircle,
  SvgWarning,
  SvgWarningSign,
} from "../../icons"
import { UserCtx } from "../../../context/userContext"
import { useToast } from "../../../hooks/useToast"
import format from "date-fns/format"
import useManageServices from "../hooks/useManageServices"
import {
  serviceLengthOptions,
  timeTypes,
} from "../../../objects/createServiceObjects"

interface Props {
  formErrors: any
  serviceValues: any
  setServiceValues: any
  serviceType: any
  isGroupService: boolean
  service: any
  handleMatchingMeetingIds: (ids: number[]) => void
}

const GroupServiceDetailsForm: React.FC<Props> = ({
  formErrors,
  serviceValues,
  setServiceValues,
  serviceType,
  service,
  handleMatchingMeetingIds,
}) => {
  const { toast } = useToast()
  const { getAvailableTimeTypes } = useManageServices()
  const [matchingMeetingIds, setMatchingMeetingIds] = useState<number[]>([])
  const { user } = useContext(UserCtx)
  const [loadingMeetings, setLoadingMeetings] = useState<boolean>(false)
  const [activeDate, setActiveDate] = useState<Date>(
    moment(new Date()).startOf("day").tz(user?.timezone?.value)?.toDate()
  )

  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [meetingDates, setMeetingDates] = useState<moment.Moment[]>([])
  const [activeFilters, setActiveFilters] = useState<FilterType[]>([
    "zoee-active-profile",
    "zoee-inactive-profile",
    "other",
  ])
  const [activeQueryMonth, setActiveQueryMonth] = useState<number | null>(null)

  const [selectedDays, setSelectedDays] = useState<any[]>(
    serviceValues?.group?.weeklyDayTimes?.map((item: { day: any }) => item?.day)
  )
  const [dayTimes, setDayTimes] = useState<any[]>(
    serviceValues?.group?.weeklyDayTimes?.map(
      (item: { time: any }) => item?.time
    )
  )

  const fullWeekdays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]
  const [weekSelections, setWeekSelections] = useState<any[]>(
    serviceValues?.group?.weeklyDayTimes?.map(
      (item: { freq: any }) => item?.freq
    )
  )

  const weekOptions = [
    { label: "First", value: 0 },
    { label: "Second", value: 1 },
    { label: "Third", value: 2 },
    { label: "Fourth", value: 3 },
  ]

  const updateMeetingDate = (date: Date, index: number) => {
    let meetingDates = serviceValues?.group?.meetingDates
    const lastMeetingDate = meetingDates[index - 1]
    if (lastMeetingDate && date <= lastMeetingDate) {
      toast.error("Meeting date must be greater than prior meeting date")
      return
    }
    meetingDates[index] = date
    setServiceValues({
      ...serviceValues,
      group: {
        ...serviceValues?.group,
        meetingDates: meetingDates,
      },
    })
  }

  const incrementDate = (date: Date) => {
    let count: any = 1
    let unit: any = "days"
    if (["individual", "bundle"].includes(serviceType)) {
      count = 7
      unit = "days"
    } else if (
      serviceType === "recurring" &&
      serviceValues?.recurring?.recurringFrequency === "monthly"
    ) {
      count = 7
      unit = "days"
    } else if (
      serviceType === "recurring" &&
      serviceValues?.recurring?.recurringFrequency === "weekly"
    ) {
      count = 2
      unit = "days"
    }
    return moment(date).add(count, unit).toDate()
  }

  const getRecurringCount = () => {
    const sessionsPerPeriod = serviceValues?.recurring?.sessionsPerPeriod
    const expireTime = "1"
    if (serviceValues?.group?.repeatBy === "custom") {
      const expireTime = serviceValues?.expiration?.expireTime
      return parseInt(sessionsPerPeriod) * parseInt(expireTime)
    }
    return parseInt(sessionsPerPeriod) * parseInt(expireTime)
  }

  const updateMonthlyDayTimeArray = () => {
    let weeklyDayTimes = []
    let weeklySessionCount = 0
    weeklySessionCount = serviceValues?.recurring?.sessionsPerPeriod
    let i = 0
    while (i < weeklySessionCount) {
      const freq = weekSelections[i]
      const day = selectedDays[i]
      const time = dayTimes[i]
      weeklyDayTimes.push({ day: day, time: time, freq: freq })
      i++
    }
    setServiceValues({
      ...serviceValues,
      group: {
        ...serviceValues.group,
        weeklyDayTimes: weeklyDayTimes,
      },
    })
  }

  const addOneYear = (date: Date) => {
    date.setFullYear(date.getFullYear() + 1)
    return date
  }

  const updateMeetingDateArray = () => {
    let meetingDates = []
    let meetingCount = 0
    if (serviceType === "individual") {
      meetingCount = 1
    } else if (serviceType === "bundle") {
      meetingCount = parseInt(serviceValues.package.packageSessions)
    } else if (serviceType === "recurring") {
      meetingCount = getRecurringCount()
    }
    let i = 0
    let date
    let lastDate
    while (i < meetingCount) {
      const doWeHaveDateAlready = !!serviceValues?.group?.meetingDates?.[i]

      if (doWeHaveDateAlready) {
        lastDate = serviceValues.group.meetingDates[i]
      }
      if (serviceValues?.group?.meetingDates?.[i]) {
        meetingDates.push(serviceValues?.group?.meetingDates?.[i])
        lastDate = date = serviceValues?.group?.meetingDates?.[i]
      } else {
        date = incrementDate(lastDate)
        meetingDates.push(date)
      }
      i++

      if (!serviceValues?.group?.meetingDates?.[i] && i !== meetingCount) {
        lastDate = date
      }
    }
    setServiceValues({
      ...serviceValues,
      group: {
        ...serviceValues.group,
        meetingDates: meetingDates,
      },
    })
  }

  const showDatepickers = () => {
    let show = false
    if (["individual", "bundle"].includes(serviceType)) {
      show = false
    }
    if (serviceType === "recurring") {
      show = true
    }
    return show
  }

  const updateGroupMeetingDetails = () => {
    if (serviceType === "recurring") {
      updateMonthlyDayTimeArray()
    } else if (
      ["bundle", "individual"].includes(serviceType) &&
      serviceValues?.package?.packageSessions > 0
    ) {
      updateMeetingDateArray()
    }
  }

  useEffect(() => {
    updateGroupMeetingDetails()
  }, [
    serviceType,
    serviceValues?.package?.packageSessions,
    serviceValues?.recurring?.sessionsPerPeriod,
    serviceValues?.expiration?.expireTime,
    selectedDays,
    dayTimes,
    weekSelections,
  ])

  const calcDuration = (endTime: any, startTime: any) => {
    const calculated = moment
      .utc(endTime)
      .diff(moment.utc(startTime), "minutes")
    let duration
    if (calculated >= 1) {
      duration = calculated
    } else {
      duration = 1
    }
    return duration
  }

  const filterMeetings = (meetings: Meeting[]) => {
    if (!meetings) {
      return
    }
    if (activeFilters.length === 0) {
      setMeetings([])
    } else if (activeFilters.length === 1) {
      if (activeFilters[0] === "zoee-active-profile") {
        setMeetings(
          meetings.filter(
            (meeting) => meeting.is_managed && meeting.belongs_to_profile
          )
        )
      } else if (activeFilters[0] === "zoee-inactive-profile") {
        setMeetings(
          meetings.filter(
            (meeting) => meeting.is_managed && !meeting.belongs_to_profile
          )
        )
      } else {
        setMeetings(meetings.filter((meeting) => !meeting.is_managed))
      }
    } else if (activeFilters.length === 2) {
      if (
        activeFilters.includes("zoee-active-profile") &&
        activeFilters.includes("zoee-inactive-profile")
      ) {
        setMeetings(meetings.filter((meeting) => meeting.is_managed))
      } else if (
        activeFilters.includes("zoee-active-profile") &&
        activeFilters.includes("other")
      ) {
        setMeetings(
          meetings.filter(
            (meeting) =>
              (meeting.is_managed && meeting.belongs_to_profile) ||
              !meeting.is_managed
          )
        )
      } else if (
        activeFilters.includes("zoee-inactive-profile") &&
        activeFilters.includes("other")
      ) {
        setMeetings(
          meetings.filter(
            (meeting) =>
              (meeting.is_managed && !meeting.belongs_to_profile) ||
              !meeting.is_managed
          )
        )
      }
    } else {
      setMeetings(meetings)
    }
  }

  const getMeetings = () => {
    if (user) {
      setLoadingMeetings(true)
      const startOfActiveDate = moment(activeDate)
        ?.startOf("day")
        ?.utc()
        ?.tz(user?.timezone?.value)
        ?.toISOString()
      const endOfSixMonths = moment(activeDate)
        ?.add(6, "month")
        ?.endOf("day")
        ?.utc()
        ?.tz(user.timezone.value)
        ?.toISOString()

      MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })
        .then((data) => {
          const matchingMeetings = data.meetings.filter(
            (m) => m.stripe_product_id === `prod_${service?.service_id}`
          )
          const meetingIds = matchingMeetings.map((m) => m.meeting_id)

          if (meetingIds.length > 0) {
            setMatchingMeetingIds(meetingIds)
          } else {
            setMatchingMeetingIds([])
          }

          const meetings: Array<Meeting> = []
          data.meetings.forEach((m) => {
            let parsedStartTime = moment
              .utc(m.start_time)
              .tz(user.timezone.value)
              .toDate()
            let parsedEndTime = null
            if (m.end_time) {
              parsedEndTime = moment
                .utc(m.end_time)
                .tz(user.timezone.value)
                .toDate()
            }
            let parsedDate = moment
              .utc(m.start_time)
              .tz(user.timezone.value)
              .toDate()

            const obj: Meeting = {
              id: m.meeting_id,
              inProgress: m.in_progress,
              meetingType: m.meeting_type,
              coach_user_id: m.coach_user_id,
              type: m.type,
              contacts: m.participants,
              meetingName: m.name,
              status: m.status,
              medium: m.medium,
              isCreator: m.is_creator,
              date: parsedDate,
              startTime: parsedStartTime,
              endTime: parsedEndTime,
              meetingEnded: m.meetingEnded,
              duration: calcDuration(m.end_time, m.start_time),
              // TODO need to know what external calendar it is coming from (e.g. google, office365) in order to use different views
              // or decide to only have one color for all non_managed calendars
              // 9/23/2022: For 2.0 launch, will just use the is_managed property to distinguish between Zoee-managed and others. Return to this property in future versions.
              calendar: `${m.is_managed ? "zoee" : "google"}`,
              timezone: user.timezone,
              service_usage_id: m.service_usage_id,
              service_details: m.service_details,
              // TODO backend for notifications not ready yet
              notifications: [],
              belongs_to_profile: m.belongs_to_profile,
              is_managed: m.is_managed,
              stripe_product_id: m.stripe_product_id,
            }

            meetings.push(obj)
          })

          const sortedMeetings = meetings.sort((a: any, b: any) => {
            const bDate: any = new Date(b.startTime)
            const aDate: any = new Date(a.startTime)
            return aDate - bDate
          })

          setMeetings(sortedMeetings)
          filterMeetings(meetings)
          setMeetingDates(createMeetingDates(sortedMeetings))
          setActiveQueryMonth(moment(activeDate).month())
          setLoadingMeetings(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          setLoadingMeetings(false)
        })
    }
  }

  const createMeetingDates = (meetings: Meeting[]) => {
    return meetings.map((meeting) => {
      return moment(meeting.date)
    })
  }

  useEffect(() => {
    if (matchingMeetingIds.length > 0) {
      handleMatchingMeetingIds(matchingMeetingIds)
    }
  }, [matchingMeetingIds, handleMatchingMeetingIds])

  useEffect(() => {
    getMeetings()
  }, [])

  return (
    <div className="md:w-[620px] main-shadow flex flex-col rounded-[16px] bg-white p-[32px] md:p-[40px]">
      <h2 className="font-bold text-[22px] mb-[20px] md:mb-[24px]">
        Group Service Details
      </h2>
      <p className="text-graySlate text-base mb-[32px]">
        Set up more of the specifics around this group service.
      </p>

      {showDatepickers() ? (
        <>
          <div className="mb-4">
            <p className="block text-sm font-medium mb-1">Start Date</p>
            <input
              type="date"
              value={serviceValues?.group?.startDate || ""}
              onChange={(e: any) => {
                setServiceValues({
                  ...serviceValues,
                  group: {
                    ...serviceValues.group,
                    startDate: e.target.value,
                  },
                })
              }}
              className={`w-full border p-2 rounded ${
                formErrors.group?.startDate ? "border-[red]" : "border-gray-300"
              }`}
              disabled={service.enrolled_count >= 1}
            />
          </div>

          <div className="flex flex-col mb-6">
            <h4 className="font-bold text-base mb-2">
              How long does this program last?
            </h4>
            <div className="flex items-center justify-between gap-2">
              <Select
                className="basic-single w-[49%]"
                classNamePrefix="select"
                options={serviceLengthOptions}
                menuPlacement="bottom"
                value={serviceLengthOptions.find(
                  (number) =>
                    number.value === serviceValues?.expiration?.expireTime
                )}
                onChange={(e: any) =>
                  setServiceValues({
                    ...serviceValues,
                    expiration: {
                      ...serviceValues.expiration,
                      expireTime: e.value,
                    },
                  })
                }
                isDisabled={service.enrolled_count >= 1}
              />
              <Select
                className="basic-single w-[49%]"
                classNamePrefix="select"
                options={getAvailableTimeTypes(
                  serviceType,
                  timeTypes,
                  serviceValues
                )}
                menuPlacement="bottom"
                value={getAvailableTimeTypes(
                  serviceType,
                  timeTypes,
                  serviceValues
                ).find(
                  (type: any) =>
                    type.value === serviceValues?.expiration?.expireType
                )}
                onChange={(e: any) =>
                  setServiceValues({
                    ...serviceValues,
                    expiration: {
                      ...serviceValues.expiration,
                      expireType: e.value,
                    },
                  })
                }
                isDisabled={service.enrolled_count >= 1}
              />
            </div>
            {formErrors.expires && (
              <div className="field-error w-full mt-1 flex items-center gap-1 text-[red]">
                <SvgWarning />
                {formErrors.expires}
              </div>
            )}
          </div>

          <div className="mb-5">
            {Array.from(
              { length: serviceValues?.recurring?.sessionsPerPeriod },
              (_, i) => {
                const isInvalid =
                  !!formErrors.weeklyDayTimes &&
                  (!serviceValues.group?.weeklyDayTimes?.[i] ||
                    serviceValues.group.weeklyDayTimes[i].day === undefined ||
                    serviceValues.group.weeklyDayTimes[i].time === undefined ||
                    serviceValues.group.weeklyDayTimes[i].freq === undefined ||
                    serviceValues.group.weeklyDayTimes[i].day === null ||
                    serviceValues.group.weeklyDayTimes[i].time === "" ||
                    serviceValues.group.weeklyDayTimes[i].freq === null)

                const redBorderClass = isInvalid
                  ? "border-[red]"
                  : "border-gray-300"

                return (
                  <React.Fragment key={i}>
                    <h4 className="font-bold text-base mb-[3px] text-graySlate">
                      {`Session #${i + 1}`}
                    </h4>

                    <div className="flex items-center gap-3 mb-3">
                      {serviceValues?.recurring?.recurringFrequency ===
                        "monthly" && (
                        <select
                          className={`w-[160px] px-3 py-2 border rounded text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary transition ${redBorderClass}`}
                          value={weekSelections[i] ?? ""}
                          onChange={(e) => {
                            const updated = [...weekSelections]
                            updated[i] = parseInt(e.target.value)
                            setWeekSelections(updated)
                          }}
                          disabled={service.enrolled_count >= 1}
                        >
                          <option value="" disabled>
                            Select Week
                          </option>
                          {weekOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}

                      <select
                        className={`w-[160px] px-3 py-2 border rounded text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary transition ${redBorderClass}`}
                        value={selectedDays[i] ?? ""}
                        onChange={(e) => {
                          const updated = [...selectedDays]
                          updated[i] = parseInt(e.target.value)
                          setSelectedDays(updated)
                        }}
                        disabled={service.enrolled_count >= 1}
                      >
                        <option value="" disabled>
                          Select Day
                        </option>
                        {fullWeekdays.map((day, index) => (
                          <option key={index} value={index}>
                            {day}
                          </option>
                        ))}
                      </select>

                      <input
                        type="time"
                        className={`p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary transition ${redBorderClass}`}
                        value={dayTimes[i]}
                        onChange={(e) => {
                          const updated = [...dayTimes]
                          updated[i] = e.target.value
                          setDayTimes(updated)
                        }}
                        disabled={service.enrolled_count >= 1}
                      />
                    </div>
                  </React.Fragment>
                )
              }
            )}
            <span className="text-sm font-medium text-gray-600 select-none italic">
              * All selected times are shown in your current time zone:{" "}
              {moment.tz(moment.tz.guess()).format("z")}
            </span>
          </div>
        </>
      ) : (
        <>
          {serviceValues?.group?.meetingDates?.map(
            (meetingDate: Date, index: number) => {
              return (
                <div key={index}>
                  <h4 className="font-bold text-base mb-[4px] text-graySlate">
                    {`Session #${index + 1}`}
                  </h4>
                  <div className="w-full relative">
                    <DatePicker
                      selected={meetingDate}
                      onChange={(date: Date) => updateMeetingDate(date, index)}
                      showTimeSelect
                      minDate={new Date()}
                      maxDate={addOneYear(new Date())}
                      dateFormat={"MM/dd/yyyy hh:mm a"}
                      required
                      className="text-center w-full pr-[25px]"
                      disabled={service.enrolled_count >= 1}
                    />
                  </div>
                </div>
              )
            }
          )}
          <span className="text-sm font-medium text-gray-600 select-none italic mt-4">
            * All selected times are shown in your current time zone:{" "}
            {moment.tz(moment.tz.guess()).format("z")}
          </span>
        </>
      )}

      <div className="w-full h-[1px] bg-grayMist my-[32px]"></div>

      {/* MAX CLIENTS */}
      <h4 className="font-bold text-base mb-[16px]">
        What is the maximum number of clients that can enroll?
      </h4>
      <div
        className={`${
          formErrors?.maxClients ? "input-error" : ""
        } flex items-center gap-[12px]`}
      >
        <input
          type="number"
          min="0"
          max="20"
          onWheel={() => (document.activeElement as HTMLElement)?.blur()}
          className="w-[54px] h-[54px] bg-white focus:outline-0 border border-1 border-grayCloud rounded-[10px] text-graySlate text-center"
          name="maximumClients"
          value={serviceValues.group.maximumClients}
          onChange={(e: any) =>
            setServiceValues({
              ...serviceValues,
              group: {
                ...serviceValues.group,
                maximumClients: e.target.value,
              },
            })
          }
          disabled={service.enrolled_count >= 1}
        />
        <span className="font-bold text-base">clients</span>
      </div>
      {formErrors?.maxClients && (
        <div className="field-error w-full mt-1 flex items-center gap-1 text-red-600">
          <SvgWarning />
          {formErrors.maxClients}
        </div>
      )}
    </div>
  )
}

export default GroupServiceDetailsForm
